import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../lib/api'
import {
  type ReviewContext,
  type ReviewSession,
  createReviewSession,
  getReviewSession,
  getStoredReviewSessions,
} from '../lib/reviewSessions'
import { InvoiceReviewSection } from './Review/sections/InvoiceReviewSection/InvoiceReviewSection'
import { LearnFromChangesDialog } from '../components/LearnFromChangesDialog'
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast'
import { NotesDialogButton } from '@/components/InvoiceNotes/NotesDialogButton'

export { createReviewSession, type ReviewContext }

export function ReviewPage() {
  const navigate = useNavigate()
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const [searchParams] = useSearchParams()
  const [invoice, setInvoice] = useState<any>(null)
  const [filteredInvoiceList, setFilteredInvoiceList] = useState<any[]>([])
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState<number>(-1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [learningDialogOpen, setLearningDialogOpen] = useState(false)
  const [pendingLearnings, setPendingLearnings] = useState<any>(null)
  const [isApproving, setIsApproving] = useState(false)
  const { token, user } = useAuth()
  const searchParamsKey = searchParams.toString()
  const pollTimerRef = useRef<number | null>(null)

  const fetchInvoiceList = useCallback(async () => {
    try {
      const invoicesResponse = await apiClient.get('/invoices/') as any
      const fetchedInvoices = invoicesResponse.invoices || []

      const sessionId = searchParams.get('sessionId')

      let filteredInvoices: any[] = []
      let reviewSession: ReviewSession | null = null

      if (sessionId) {
        reviewSession = getReviewSession(sessionId)
        if (reviewSession) {
          const { type, invoiceIds } = reviewSession.context
          if (type === 'selected' || type === 'all_pending') {
            filteredInvoices = fetchedInvoices.filter((inv: any) => invoiceIds.includes(inv.id))
          }
        }
      }

      setFilteredInvoiceList(filteredInvoices)

      if (invoiceId) {
        const currentIndex = filteredInvoices.findIndex((inv: any) => inv.id === invoiceId)
        setCurrentInvoiceIndex(currentIndex)
      }

      const remainingCount = reviewSession?.context.invoiceIds.length ?? filteredInvoices.length
      if (remainingCount > 0) {
        localStorage.setItem('pendingReviewCount', remainingCount.toString())
      } else {
        localStorage.removeItem('pendingReviewCount')
      }

      return filteredInvoices
    } catch (err: any) {
      console.error('Error fetching invoice list:', err)
      return []
    }
  }, [invoiceId, searchParamsKey])

  const fetchInvoiceData = useCallback(async () => {
    if (!invoiceId) {
      setError('No invoice ID provided')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      await fetchInvoiceList()

      const invoiceData = await apiClient.get(`/invoices/${invoiceId}`) as any

      const invoiceObj = {
        ...invoiceData.invoice,
        extractedData: invoiceData.extracted_data || [],
        currency_rate_info: invoiceData.currency_rate_info || null,
        currency_representations: invoiceData.currency_representations || [],
      }
      setInvoice(invoiceObj)
      setIsProcessing(invoiceObj.status === 'processing' || invoiceObj.status === 'uploaded')
    } catch (err: any) {
      setError(err.message || 'Failed to load invoice data')
      console.error('Error loading invoice:', err)
    } finally {
      setLoading(false)
    }
  }, [fetchInvoiceList, invoiceId, token])

  useEffect(() => {
    if (!isProcessing || !invoiceId) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
      return
    }

    pollTimerRef.current = window.setInterval(async () => {
      try {
        const data = await apiClient.get(`/invoices/${invoiceId}`) as any
        const status = data.invoice?.status
        if (status && status !== 'processing' && status !== 'uploaded') {
          setIsProcessing(false)
          setInvoice({
            ...data.invoice,
            extractedData: data.extracted_data || [],
            currency_rate_info: data.currency_rate_info || null,
            currency_representations: data.currency_representations || [],
          })
        }
      } catch {
        // Silently retry on next interval
      }
    }, 3000)

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [isProcessing, invoiceId])

  useEffect(() => {
    void fetchInvoiceData()
  }, [fetchInvoiceData])

  const refreshInvoiceSilently = useCallback(async () => {
    if (!invoiceId) return
    try {
      const invoiceData = await apiClient.get(`/invoices/${invoiceId}`) as any
      setInvoice({
        ...invoiceData.invoice,
        extractedData: invoiceData.extracted_data || [],
        currency_rate_info: invoiceData.currency_rate_info || null,
        currency_representations: invoiceData.currency_representations || [],
      })
    } catch (err) {
      console.error('Silent refresh failed:', err)
    }
  }, [invoiceId])

  const executeApproval = async (learnFromChanges: boolean | null, overrideDecisions?: Record<string, string>) => {
    try {
      setIsApproving(true)
      await apiClient.put(`/invoices/${invoice?.id}/status`, {
        status: 'reviewed',
        learn_from_changes: learnFromChanges,
        ...(overrideDecisions && { override_decisions: overrideDecisions }),
      })

      const sessionId = searchParams.get('sessionId')
      if (sessionId && invoice?.id) {
        const session = getReviewSession(sessionId)
        if (session) {
          const updatedContext: ReviewContext = {
            ...session.context,
            invoiceIds: session.context.invoiceIds.filter(id => id !== invoice.id)
          }

          const sessions = getStoredReviewSessions()
          const storedSession = sessions[sessionId]
          if (storedSession) {
            const currentCompleted = typeof storedSession.completedCount === 'number' ? storedSession.completedCount : 0
            const fallbackInitialTotal = currentCompleted + updatedContext.invoiceIds.length + 1
            const normalizedInitialTotal = typeof storedSession.initialTotal === 'number' && storedSession.initialTotal > 0
              ? storedSession.initialTotal
              : fallbackInitialTotal

            storedSession.context = updatedContext
            storedSession.initialTotal = normalizedInitialTotal
            storedSession.completedCount = Math.min(currentCompleted + 1, normalizedInitialTotal)

            localStorage.setItem('review_sessions', JSON.stringify(sessions))
          }

          setFilteredInvoiceList(prev => {
            const updated = prev.filter(inv => inv.id !== invoice.id)
            const remainingCount = updatedContext.invoiceIds.length
            if (remainingCount > 0) {
              localStorage.setItem('pendingReviewCount', remainingCount.toString())
            } else {
              localStorage.removeItem('pendingReviewCount')
            }

            const returnUrl = sessionStorage.getItem('processingReturnUrl') || '/invoices/processing'

            if (updated.length === 0) {
              navigate(returnUrl)
            } else {
              const nextInvoice = updated[Math.min(currentInvoiceIndex, updated.length - 1)]
              if (nextInvoice) {
                navigate(`/review/${nextInvoice.id}?sessionId=${sessionId}`)
              } else {
                navigate(returnUrl)
              }
            }

            return updated
          })
        } else {
          navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
        }
      } else {
        navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
      }

      showSuccessToast('Invoice marked as reviewed!')
    } catch (err) {
      console.error('Error approving invoice:', err)
      showErrorToast(`Error updating invoice status: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsApproving(false)
      setLearningDialogOpen(false)
    }
  }

  const handleApprove = async () => {
    const preference = user?.learning_preference || 'always_ask'

    if (preference === 'never_learn') {
      await executeApproval(false)
      return
    }

    if (preference === 'always_learn') {
      await executeApproval(true)
      return
    }

    try {
      const learnings = await apiClient.get(`/invoices/${invoice?.id}/pending-learnings`) as any
      if (learnings?.has_changes) {
        setPendingLearnings(learnings)
        setLearningDialogOpen(true)
      } else {
        await executeApproval(null)
      }
    } catch {
      await executeApproval(null)
    }
  }

  const handleLearningDialogConfirm = async (learn: boolean, rememberChoice: boolean, overrideDecisions?: Record<string, string>) => {
    if (rememberChoice) {
      try {
        const newPref = learn ? 'always_learn' : 'never_learn'
        await apiClient.patch('/auth/me/preferences', { learning_preference: newPref })
        const storedUser = localStorage.getItem('user_data')
        if (storedUser) {
          const parsed = JSON.parse(storedUser)
          parsed.learning_preference = newPref
          localStorage.setItem('user_data', JSON.stringify(parsed))
        }
      } catch {
        // Non-critical, continue with approval
      }
    }
    await executeApproval(learn, overrideDecisions)
  }

  const handleNavigateToNext = () => {
    if (filteredInvoiceList.length === 0) return

    let nextIndex = currentInvoiceIndex + 1
    if (nextIndex >= filteredInvoiceList.length) {
      nextIndex = 0
    }

    const nextInvoice = filteredInvoiceList[nextIndex]
    if (nextInvoice) {
      const sessionId = searchParams.get('sessionId')
      const searchParamsObj = new URLSearchParams()
      if (sessionId) {
        searchParamsObj.set('sessionId', sessionId)
      }
      const queryString = searchParamsObj.toString()
      const url = queryString ? `/review/${nextInvoice.id}?${queryString}` : `/review/${nextInvoice.id}`
      navigate(url)
    }
  }

  const handleNavigateToPrevious = () => {
    if (filteredInvoiceList.length === 0) return

    let previousIndex = currentInvoiceIndex - 1
    if (previousIndex < 0) {
      previousIndex = filteredInvoiceList.length - 1
    }

    const previousInvoice = filteredInvoiceList[previousIndex]
    if (previousInvoice) {
      const sessionId = searchParams.get('sessionId')
      const searchParamsObj = new URLSearchParams()
      if (sessionId) {
        searchParamsObj.set('sessionId', sessionId)
      }
      const queryString = searchParamsObj.toString()
      const url = queryString ? `/review/${previousInvoice.id}?${queryString}` : `/review/${previousInvoice.id}`
      navigate(url)
    }
  }

  const handleSkip = () => {
    if (filteredInvoiceList.length <= 1) {
      navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
      return
    }
    handleNavigateToNext()
  }

  const handlePrevious = () => {
    if (filteredInvoiceList.length <= 1) {
      navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
      return
    }
    handleNavigateToPrevious()
  }

  if (loading) {
    return (
      <div className="page-shell">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-300">Loading invoice...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="page-shell">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Invoice not found'}</p>
          <button
            onClick={() => navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            &larr; Back to Invoice Processing
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="mb-6">
        <button
          onClick={() => navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          &larr; Back to Invoice Processing
        </button>
        <div className="flex items-center justify-between mt-4">
          <h1 className="text-2xl font-bold text-foreground page-main-heading">Invoice Review</h1>
          {invoiceId && (
            <div className="flex items-center gap-2">
              <NotesDialogButton invoiceId={invoiceId} />
            </div>
          )}
        </div>
      </div>

      <InvoiceReviewSection
        invoice={invoice}
        isProcessing={isProcessing}
        onInvoiceRefresh={fetchInvoiceData}
        onInvoiceRefreshSilent={refreshInvoiceSilently}
        onProcessingStarted={() => setIsProcessing(true)}
        onFieldPatch={(updatedField: Record<string, unknown>) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              extractedData: prev.extractedData.map((f: any) =>
                f.id === updatedField.id ? { ...f, ...updatedField } : f
              ),
            };
          });
        }}
        onRepresentationUpdate={(updatedRepr) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              currency_representations: prev.currency_representations?.map((r: any) =>
                r.id === updatedRepr.id ? updatedRepr : r
              ) ?? [],
            };
          });
        }}
        onLineItemsUpdated={(extractedData, representation, additionalMetadata) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            const updated: any = {
              ...prev,
              extractedData: extractedData,
            };
            if (representation) {
              updated.currency_representations = prev.currency_representations?.map((r: any) =>
                r.id === representation.id ? representation : r
              ) ?? [representation];
            }
            if (additionalMetadata) {
              updated.additional_metadata = additionalMetadata;
            }
            return updated;
          });
        }}
        onErpMetadataPatched={(lineItemGroup, metadata) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            const prevMeta = prev.additional_metadata ?? {};
            const erp = { ...(prevMeta.erp ?? {}) };
            const lineItems = [...(erp.line_items ?? [])];
            const idx = lineItems.findIndex((li: any) => li.line_item_group === lineItemGroup);
            const entry = { line_item_group: lineItemGroup, ...metadata };
            if (idx >= 0) {
              lineItems[idx] = entry;
            } else {
              lineItems.push(entry);
            }
            erp.line_items = lineItems;
            return {
              ...prev,
              additional_metadata: { ...prevMeta, erp },
            };
          });
        }}
        onFieldUpdate={async (fieldId, newValue) => {
          if (!invoice) return;

          try {
            const response = await apiClient.fetch(`/invoices/${invoice.id}/fields/${fieldId}`, {
              method: 'PUT',
              body: JSON.stringify({
                field_value: newValue,
                is_corrected: true
              })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.detail || `Failed to update field: ${response.statusText}`);
            }

            const result = await response.json();

            const updatedField = result.field;
            if (updatedField) {
              setInvoice((prev: any) => {
                if (!prev) return prev;
                const rows = prev.extractedData ?? [];
                const existingIdx = rows.findIndex(
                  (f: any) =>
                    f.id === updatedField.id ||
                    (f.field_name &&
                      updatedField.field_name &&
                      f.field_name.toLowerCase() === updatedField.field_name.toLowerCase())
                );
                const nextRows =
                  existingIdx >= 0
                    ? rows.map((f: any, i: number) =>
                        i === existingIdx ? { ...f, ...updatedField } : f
                      )
                    : [...rows, updatedField];
                const next: any = {
                  ...prev,
                  extractedData: nextRows,
                };
                if (result.currency_rate_info) {
                  next.currency_rate_info = result.currency_rate_info;
                }
                return next;
              });
            }

            showSuccessToast('Field updated successfully!');

          } catch (error) {
            console.error('Error updating field:', error);
            showErrorToast(`Error updating field: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }}
        onApprove={handleApprove}
        onSkip={handleSkip}
        onPrevious={handlePrevious}
        currentInvoiceIndex={currentInvoiceIndex}
        totalInvoices={filteredInvoiceList.length || (invoice ? 1 : 0)}
      />

      {pendingLearnings && (
        <LearnFromChangesDialog
          open={learningDialogOpen}
          onClose={() => setLearningDialogOpen(false)}
          onConfirm={handleLearningDialogConfirm}
          learnings={pendingLearnings}
          isLoading={isApproving}
          clientId={invoice?.client?.id}
          isIncome={invoice?.additional_metadata?.counterparty?.matched_role === 'seller'}
          fieldOptions={invoice?.additional_metadata?.field_options}
        />
      )}
    </div>
  )
}
